import { useQuery } from '@apollo/client';
import {
  ADMIN_COMPANIES_PAGE_SIZE,
  AdminCompaniesSearchParams,
  AdminCompaniesVars,
  ApiAdminCompaniesResponse,
  ApiAdminCompaniesVars,
  DEFAULT_ADMIN_COMPANIES_VARS,
  DEFAULT_PAGE,
  UseAdminCompanies,
  UseAdminCompaniesVars,
  isAdminCompaniesSorting
} from './types';
import { GET_COMPANIES } from './queries';
import { AdminCompaniesData } from '.';
import { useCallback, useContext, useMemo, useState } from 'react';
import { isString, updateQueryParams, useQueryParams } from 'src/helpers';
import { AdminCompaniesContext } from './context';

export function useAdminCompaniesVars(): UseAdminCompaniesVars {
  const ctx = useContext(AdminCompaniesContext);
  if (!ctx) {
    throw new Error('not in AdminCompaniesContext provider');
  }

  return ctx;
}
export function useAdminCompaniesCtx(): UseAdminCompaniesVars {
  const defaultArgs = DEFAULT_ADMIN_COMPANIES_VARS;
  const page = DEFAULT_PAGE;
  const pageSize = ADMIN_COMPANIES_PAGE_SIZE;

  const qp = useQueryParams();

  const [args, setArgs] = useState<AdminCompaniesVars>(() => ({
    sortBy: isAdminCompaniesSorting(qp[AdminCompaniesSearchParams.sortBy])
      ? qp[AdminCompaniesSearchParams.sortBy]
      : undefined,
    id: isString(qp[AdminCompaniesSearchParams.id]) ? qp[AdminCompaniesSearchParams.id] : undefined,
    name: isString(qp[AdminCompaniesSearchParams.name]) ? qp[AdminCompaniesSearchParams.name] : undefined
  }));

  const vars: ApiAdminCompaniesVars = useMemo(
    () => ({
      sortBy: args.sortBy
        ? {
            field: args.sortBy.column,
            direction: args.sortBy.sort
          }
        : undefined,
      amount: pageSize,
      offset: (page - 1) * pageSize,
      id: !!args.id ? args.id : undefined,
      name: !!args.name ? args.name : undefined
    }),
    [args.sortBy, args.id, args.name, pageSize, page]
  );

  const updateArgs = useCallback((changedArgs: Partial<AdminCompaniesVars>) => {
    updateQueryParams(changedArgs);
    setArgs(currentState => ({ ...currentState, ...changedArgs }));
  }, []);

  return useMemo(() => [{ args, vars, initArgs: defaultArgs }, updateArgs], [args, vars, defaultArgs, updateArgs]);
}

export function useAdminCompanies(): UseAdminCompanies {
  const [{ vars }] = useAdminCompaniesVars();
  const query = useQuery<ApiAdminCompaniesResponse, ApiAdminCompaniesVars>(GET_COMPANIES, {
    variables: vars,
    skip: !vars,
    notifyOnNetworkStatusChange: true
  });

  return useMemo(
    () => ({
      ...query,
      data: query.data ? new AdminCompaniesData(query.data.getCompanies) : undefined,
      loading:
        (query.loading || query.data === undefined || query.networkStatus === 4 || query.networkStatus === 2) &&
        query.networkStatus !== 3,
      fetchingMore: query.networkStatus === 3
    }),
    [query]
  );
}
